import {useState} from "react";
import {
    Box,
    Button, Card, CardContent, FormControl, FormHelperText,
    FormLabel,
    Modal,
    ModalClose,
    ModalDialog,
    Radio,
    RadioGroup,
    Sheet, Stack, styled,
    SvgIcon, Textarea,
    Typography
} from "@mui/joy";
import {radioClasses} from '@mui/joy/Radio';
import {CheckCircleRounded} from "@mui/icons-material";
import {StlViewer} from "react-stl-viewer";
import {PROCESSES} from "../PROCESSES";

const VisuallyHiddenInput = styled('input')`
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  height: 1px;
  overflow: hidden;
  position: absolute;
  bottom: 0;
  left: 0;
  white-space: nowrap;
  width: 1px;
`;

export default function PartModal(props) {
    const {open, onClose, partSubmit, basis, editing} = props;

    const [jobType, setJobType] = useState(basis.jobType || null);

    const [designFile, setDesignFile] = useState(basis.file);
    const [designUrl, setDesignUrl] = useState(basis.url);

    const [partMaterial, setPartMaterial] = useState(basis.material || null);
    const [partMaterialVersion, setPartMaterialVersion] = useState(basis.materialVersion || null);

    const [partNotes, setPartNotes] = useState(basis.notes || '');

    const onUpload = (e) => {
        const files = e.target.files;

        setDesignFile(files[0]);
        setDesignUrl(URL.createObjectURL(files[0]));
    };

    const finishPart = () => {
        if (!designFile) {
            return;
        }

        partSubmit({
            modelName: designFile.name,
            file: designFile,
            url: designUrl,
            jobType: jobType,
            material: partMaterial,
            materialVersion: partMaterialVersion,
            notes: partNotes,
        });
    };

    return (
        <Modal
            open={open}
            onClose={onClose}
        >
            <ModalDialog
                sx={{
                    p: '30px',
                    overflowY: 'scroll',
                }}
            >
                <ModalClose />

                <Typography
                    element="h2"
                    fontSize="25px"
                    fontFamily="'Inter', sans-serif"
                    fontWeight="400"
                >
                    {editing ? 'Edit' : 'Add'} Part
                </Typography>

                <Box sx={{ mt: '20px', mb: '40px', display: 'flex', justifyContent: 'center' }}>
                    <RadioGroup
                        aria-label="job-type"
                        value={jobType}
                        onChange={(e) => {
                            setJobType(e.target.value);
                            setPartMaterial(null);
                        }}
                        overlay
                        name="job-type"
                        sx={{
                            flexDirection: 'row',
                            gap: 2,
                            [`& .${radioClasses.checked}`]: {
                                [`& .${radioClasses.action}`]: {
                                    inset: -1,
                                    border: '3px solid',
                                    borderColor: 'primary.500',
                                },
                            },
                            [`& .${radioClasses.radio}`]: {
                                display: 'contents',
                                '& > svg': {
                                    zIndex: 2,
                                    position: 'absolute',
                                    top: '-8px',
                                    right: '-8px',
                                    bgcolor: 'background.surface',
                                    borderRadius: '50%',
                                },
                            },
                        }}
                    >
                        {Object.entries(PROCESSES).map(([value, {name, image}]) => (
                            <Sheet
                                key={value}
                                variant="outlined"
                                sx={{
                                    borderRadius: 'md',
                                    boxShadow: 'sm',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    gap: 1.5,
                                    p: 2,
                                    minWidth: 120,
                                }}
                            >
                                <Radio id={value} value={value} checkedIcon={<CheckCircleRounded />} />
                                <div
                                    style={{
                                        width: '130px',
                                        height: '110px',
                                        objectFit: 'fill',
                                        background: `url('${image}')`,
                                        backgroundSize: 'cover',
                                        borderRadius: '7px'
                                    }}
                                />
                                <FormLabel htmlFor={value}>{name}</FormLabel>
                            </Sheet>
                        ))}
                    </RadioGroup>
                </Box>

                <Typography
                    element="h3"
                    fontSize="20px"
                >
                    Design
                </Typography>

                {designUrl && (
                    <Card>
                        <CardContent>
                            <Stack direction="horizontal">
                                {designFile.name.toLowerCase().endsWith('.stl') ?
                                    <StlViewer
                                        shadows
                                        style={{
                                            width: '200px',
                                            height: '200px',
                                            border: 'solid 1px #ddd',
                                            borderRadius: '7px',
                                            backgroundColor: '#fff',
                                        }}
                                        cameraProps={{
                                            initialPosition: {
                                                distance: 1.7,
                                            }
                                        }}
                                        url={designUrl}
                                    /> :
                                    <img
                                        style={{
                                            width: '200px',
                                            height: '200px',
                                            border: 'solid 1px #ddd',
                                            borderRadius: '7px',
                                            backgroundColor: '#fff',
                                        }}
                                        alt=""
                                        src={designUrl}
                                    />
                                }
                                <Box
                                    sx={{
                                        ml: '20px'
                                    }}
                                >
                                    <Typography
                                        fontSize="18px"
                                        fontWeight="500"
                                    >
                                        {designFile.name}
                                    </Typography>
                                    <Typography>
                                        {Math.floor(designFile.size / 1000)} KB
                                    </Typography>
                                </Box>
                            </Stack>
                        </CardContent>
                    </Card>
                )}

                <FormControl>
                    <Button
                        component="label"
                        role={undefined}
                        tabIndex={-1}
                        variant="outlined"
                        color="neutral"
                        startDecorator={
                            <SvgIcon>
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    strokeWidth={1.5}
                                    stroke="currentColor"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M12 16.5V9.75m0 0l3 3m-3-3l-3 3M6.75 19.5a4.5 4.5 0 01-1.41-8.775 5.25 5.25 0 0110.233-2.33 3 3 0 013.758 3.848A3.752 3.752 0 0118 19.5H6.75z"
                                    />
                                </svg>
                            </SvgIcon>
                        }
                        sx={{
                            mb: '10px'
                        }}
                    >
                        Select design
                        <VisuallyHiddenInput
                            type="file"
                            accept=".stl,.svg"
                            onChange={onUpload}
                        />
                    </Button>

                    <FormHelperText>
                        For 3D printing and molds, please upload a Standard Tesselation File (STL). For laser cutting
                        and other 2D operations, please upload a Scalable Vector Graphics (SVG) file. STL files can be
                        generated in Fusion 360 by going to File > 3D Print. All 3D modeling/CAD software are capable of exporting STL files.
                    </FormHelperText>
                </FormControl>

                {jobType !== undefined && jobType !== null && (
                    <>
                        <Typography
                            element="h3"
                            fontSize="20px"
                            mt="20px"
                        >
                            Material
                        </Typography>

                        <RadioGroup
                            aria-labelledby="storage-label"
                            size="lg"
                            sx={{ gap: 1.5 }}
                            value={partMaterial}
                            onChange={(e) => setPartMaterial(e.target.value)}
                        >
                            {Object.entries(PROCESSES[jobType]?.materials || []).map(([value, {name, description}]) => (
                                <Sheet key={value} sx={{ p: 2, borderRadius: 'md', boxShadow: 'sm' }}>
                                    <Radio
                                        label={
                                            <Stack>
                                                <Typography>
                                                    {name}
                                                </Typography>
                                                <Typography
                                                    textColor="#555 !important"
                                                    fontWeight="400"
                                                    sx={{
                                                        zIndex: 300
                                                    }}
                                                >
                                                    {description}
                                                </Typography>
                                            </Stack>
                                        }
                                        overlay
                                        disableIcon
                                        value={value}
                                        slotProps={{
                                            label: ({ checked }) => ({
                                                sx: {
                                                    fontWeight: 'lg',
                                                    fontSize: 'md',
                                                    color: checked ? 'text.primary' : 'text.secondary',
                                                },
                                            }),
                                            action: ({ checked }) => ({
                                                sx: (theme) => ({
                                                    ...(checked && {
                                                        '--variant-borderWidth': '2px',
                                                        '&&': {
                                                            // && to increase the specificity to win the base :hover styles
                                                            borderColor: theme.vars.palette.primary[500],
                                                        },
                                                    }),
                                                }),
                                            }),
                                        }}
                                    />
                                </Sheet>
                            ))}
                        </RadioGroup>
                    </>
                )}

                {PROCESSES[jobType]?.materials[partMaterial]?.versions !== undefined && (
                    <>
                        <Typography
                            element="h3"
                            fontSize="20px"
                            mt="20px"
                        >
                            Version
                        </Typography>

                        <RadioGroup
                            aria-labelledby="storage-label"
                            size="lg"
                            sx={{ gap: 1.5 }}
                            value={partMaterialVersion}
                            onChange={(e) => setPartMaterialVersion(e.target.value)}
                        >
                            {Object.entries(PROCESSES[jobType]?.materials[partMaterial]?.versions).map(([value, {name}]) => (
                                <Sheet key={value} sx={{ p: 2, borderRadius: 'md', boxShadow: 'sm' }}>
                                    <Radio
                                        label={name}
                                        overlay
                                        disableIcon
                                        value={value}
                                        slotProps={{
                                            label: ({ checked }) => ({
                                                sx: {
                                                    fontWeight: 'lg',
                                                    fontSize: 'md',
                                                    color: checked ? 'text.primary' : 'text.secondary',
                                                },
                                            }),
                                            action: ({ checked }) => ({
                                                sx: (theme) => ({
                                                    ...(checked && {
                                                        '--variant-borderWidth': '2px',
                                                        '&&': {
                                                            // && to increase the specificity to win the base :hover styles
                                                            borderColor: theme.vars.palette.primary[500],
                                                        },
                                                    }),
                                                }),
                                            }),
                                        }}
                                    />
                                </Sheet>
                            ))}
                        </RadioGroup>
                    </>
                )}

                <Typography
                    element="h3"
                    fontSize="20px"
                    mt="20px"
                >
                    Additional Notes
                </Typography>

                <FormControl>
                    <Textarea
                        placeholder="20% infill requested, blue filament if possible."
                        minRows={3}
                        value={partNotes}
                        onChange={(e) => setPartNotes(e.target.value)}
                    />
                    <FormHelperText>
                        Indicate preferences for processing settings, color, infill density, orientation, etc., and we
                        will do our best to accommodate. Note that we may not be able to fulfill every request due to
                        lack of resources, time, or other limiting factors.
                    </FormHelperText>
                </FormControl>

                <Button
                    sx={{
                        mt: '20px'
                    }}
                    onClick={finishPart}
                >
                    Finish Part
                </Button>
            </ModalDialog>
        </Modal>
    );
}